import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CalendarIcon, Save, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import MensagensTab from "@/components/MensagensTab";
import { OrcamentoProdutosTab } from "@/components/OrcamentoProdutosTab";

const formSchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
  id_projeto: z.string().optional(),
  escopo: z.string().optional(),
  data_validade: z.date().optional(),
  status: z.string().optional(),
  id_cliente: z.string().optional(),
  id_empresa: z.string().optional(),
  id_fornecedor: z.string().optional(),
  id_local: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface FilterOptions {
  projetos: Array<{ id: string; nome: string; valor_maximo?: number }>;
  clientes: Array<{ id: string; nome: string }>;
  empresas: Array<{ id: string; razao_social: string }>;
  fornecedores: Array<{ id: string; razao_social: string }>;
  locais: Array<{ id: string; nome: string }>;
}

export default function OrcamentoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [orcamento, setOrcamento] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    projetos: [],
    clientes: [],
    empresas: [],
    fornecedores: [],
    locais: [],
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: "",
      id_projeto: "",
      escopo: "",
      status: "rascunho",
      id_cliente: "",
      id_empresa: "",
      id_fornecedor: "",
      id_local: "",
    },
  });

  const loadFilterOptions = async () => {
    try {
      const [projetos, clientes, empresas, fornecedores, locais] = await Promise.all([
        supabase.from("projetos").select("id, nome, valor_maximo"),
        supabase.from("clientes").select("id, nome").eq("ativo", true),
        supabase.from("empresas").select("id, razao_social").eq("ativo", true),
        supabase.from("fornecedores").select("id, razao_social").eq("ativo", true),
        supabase.from("locais").select("id, nome").eq("ativo", true),
      ]);

      setFilterOptions({
        projetos: projetos.data || [],
        clientes: clientes.data || [],
        empresas: empresas.data || [],
        fornecedores: fornecedores.data || [],
        locais: locais.data || [],
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar opções",
        variant: "destructive",
      });
    }
  };

  const loadOrcamento = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("orcamentos")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      // @ts-ignore - Temporary fix for schema mismatch
      setOrcamento(data);
      form.reset({
        nome: (data as any).nome || '',
        id_projeto: (data as any).id_projeto || "",
        escopo: (data as any).escopo || "",
        data_validade: (data as any).data_validade ? new Date((data as any).data_validade) : undefined,
        status: data.status || "rascunho",
        id_cliente: (data as any).id_cliente || "",
        id_empresa: (data as any).id_empresa || "",
        id_fornecedor: (data as any).id_fornecedor || "",
        id_local: (data as any).id_local || "",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar orçamento",
        variant: "destructive",
      });
      navigate("/intranet/propostas/orcamentos");
    }
  };

  useEffect(() => {
    loadFilterOptions();
    if (isEditing) {
      loadOrcamento();
    }
  }, [id]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);

    try {
      const payload = {
        ...data,
        id_projeto: data.id_projeto || null,
        id_cliente: data.id_cliente || null,
        id_empresa: data.id_empresa || null,
        id_fornecedor: data.id_fornecedor || null,
        id_local: data.id_local || null,
        data_validade: data.data_validade ? format(data.data_validade, "yyyy-MM-dd") : null,
        updated_by: isEditing ? (await supabase.auth.getUser()).data.user?.id : undefined,
        created_by: !isEditing ? (await supabase.auth.getUser()).data.user?.id : undefined,
      };

      let result;
      if (isEditing) {
        // @ts-ignore - Temporary fix for schema mismatch
        result = await supabase
          .from("orcamentos")
          .update(payload as any)
          .eq("id", id)
          .select()
          .single();
      } else {
        // @ts-ignore - Temporary fix for schema mismatch
        result = await supabase
          .from("orcamentos")
          .insert(payload as any)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      toast({
        title: "Sucesso",
        description: `Orçamento ${isEditing ? "atualizado" : "criado"} com sucesso`,
      });

      navigate(`/intranet/propostas/orcamentos/${result.data.id}`);
    } catch (error) {
      toast({
        title: "Erro",
        description: `Erro ao ${isEditing ? "atualizar" : "criar"} orçamento`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/intranet/propostas/orcamentos")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? "Editar Orçamento" : "Novo Orçamento"}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? "Edite as informações do orçamento" : "Crie um novo orçamento"}
          </p>
        </div>
      </div>

      <Tabs defaultValue="dados-basicos" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dados-basicos">Dados Básicos</TabsTrigger>
          <TabsTrigger value="relacionamentos">Relacionamentos</TabsTrigger>
          <TabsTrigger value="produtos" disabled={!isEditing}>Produtos</TabsTrigger>
          <TabsTrigger value="mensagens" disabled={!isEditing}>Mensagens</TabsTrigger>
        </TabsList>

        <TabsContent value="dados-basicos">
          <Card>
            <CardHeader>
              <CardTitle>Dados Básicos</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="nome"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome *</FormLabel>
                          <FormControl>
                            <Input placeholder="Nome do orçamento" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="id_projeto"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Projeto</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um projeto" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filterOptions.projetos.map((projeto) => (
                                <SelectItem key={projeto.id} value={projeto.id}>
                                  {projeto.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="data_validade"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Data de Validade</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP")
                                  ) : (
                                    <span>Selecione uma data</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < new Date()}
                                initialFocus
                                className="pointer-events-auto"
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="rascunho">Rascunho</SelectItem>
                              <SelectItem value="pendente">Pendente</SelectItem>
                              <SelectItem value="aprovado">Aprovado</SelectItem>
                              <SelectItem value="rejeitado">Rejeitado</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="escopo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Escopo</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Descreva o escopo do orçamento"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-4">
                    <Button type="submit" disabled={loading}>
                      <Save className="mr-2 h-4 w-4" />
                      {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="relacionamentos">
          <Card>
            <CardHeader>
              <CardTitle>Relacionamentos</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="id_cliente"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cliente</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um cliente" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filterOptions.clientes.map((cliente) => (
                                <SelectItem key={cliente.id} value={cliente.id}>
                                  {cliente.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="id_empresa"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Empresa</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione uma empresa" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filterOptions.empresas.map((empresa) => (
                                <SelectItem key={empresa.id} value={empresa.id}>
                                  {empresa.razao_social}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="id_fornecedor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Fornecedor</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um fornecedor" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filterOptions.fornecedores.map((fornecedor) => (
                                <SelectItem key={fornecedor.id} value={fornecedor.id}>
                                  {fornecedor.razao_social}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="id_local"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Local</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione um local" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {filterOptions.locais.map((local) => (
                                <SelectItem key={local.id} value={local.id}>
                                  {local.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button type="submit" disabled={loading}>
                      <Save className="mr-2 h-4 w-4" />
                      {loading ? "Salvando..." : "Salvar Relacionamentos"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="produtos">
          {isEditing && orcamento && (
            <OrcamentoProdutosTab orcamentoId={id!} projetoId={orcamento.id_projeto} />
          )}
        </TabsContent>

        <TabsContent value="mensagens">
          {isEditing && (
            <MensagensTab tela="orcamentos" idRegistro={id!} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}